import { Injectable, NgZone, Optional } from '@angular/core';
import {
  from,
  fromEvent,
  interval,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  timer
} from 'rxjs';
import {
  bufferTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  scan,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { JTUserIdleConfig } from './user-idle.config';
import { retry } from 'rxjs/operators';
import { CommonService } from '../_services/app.common.service';

/**
 * User's idle service.
 */
@Injectable({
  providedIn: 'root'
})
export class JTUserIdleService {
  ping$: Observable<any> = of(null);

  /**
   * Events that can interrupts user's inactivity timer.
   */
  protected activityEvents$!: Observable<any>;

  protected timerStart$ = new Subject<boolean>();
  protected idleDetected$ = new Subject<boolean>();
  protected timeout$ = new Subject<boolean>();
  protected idle$: Observable<any> = of(null);
  protected timer$: Observable<any> = of(null);
  /**
   * Idle value in milliseconds.
   * Default equals to 10 minutes.
   */
  protected idleMillisec = 600 * 1000;
  /**
   * Idle buffer wait time milliseconds to collect user action
   * Default equals to 1 Sec.
   */
  protected idleSensitivityMillisec = 1000;
  /**
   * Timeout value in seconds.
   * Default equals to 5 minutes.
   */
  protected timeout = 300;
  /**
   * Ping value in milliseconds.
   * Default equals to 2 minutes.
   */
  protected pingMillisec = 120 * 1000;
  /**
   * Timeout status.
   */
  protected isTimeout: boolean=false;
  /**
   * Timer of user's inactivity is in progress.
   */
  protected isInactivityTimer: boolean = false;
  protected isIdleDetected: boolean = false;

  protected idleSubscription: Subscription = Subscription.EMPTY;

  constructor(@Optional() config: JTUserIdleConfig, private _ngZone: NgZone, private common: CommonService) {
    if (config) {
      this.setConfig(config);
    }


  }

  /**
   * Start watching for user idle and setup timer and ping.
   */
  // startWatching() {
  //  if (!this.activityEvents$) {
  //    this.activityEvents$ = merge(
  //      fromEvent(window, 'click'),
  //      fromEvent(document, 'keydown')
  //    );
  //  }

  //  this.idle$ = from(this.activityEvents$);

  //  if (this.idleSubscription) {
  //    this.idleSubscription.unsubscribe();
  //  }

  //  // If any of user events is not active for idle-seconds when start timer.
  //  this.idleSubscription = this.idle$
  //    .pipe(
  //      bufferTime(this.idleSensitivityMillisec), // Starting point of detecting of user's inactivity
  //    filter(
  //        arr => !arr.length && !this.isIdleDetected && !this.isInactivityTimer
  //      ),
  //      tap(() => {
  //        this.isIdleDetected = true;
  //        this.idleDetected$.next(true);
  //      }),
  //      switchMap(() =>
  //        this._ngZone.runOutsideAngular(() =>
  //          interval(1000).pipe(
  //            takeUntil(
  //              merge(
  //                this.activityEvents$,
  //                timer(this.idleMillisec).pipe(
  //                  tap(() => {
  //
  //                    this.isInactivityTimer = true;
  //                    this.timerStart$.next(true);
  //                  })
  //                )
  //              )
  //            ),
  //            finalize(() => {
  //
  //              this.isIdleDetected = false;
  //              this.idleDetected$.next(false);
  //            })
  //          )
  //        )
  //      )
  //    )
  //    .subscribe();

  //  this.setupTimer(this.timeout);
  //  this.setupPing(this.pingMillisec);
  // }

  startWatching() {
    if (!this.activityEvents$) {
      this.startIdleTimer();
    }

    this.idle$ = from(this.activityEvents$);
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }

    // If any of user events is not active for idle-seconds when start timer.
    this.idleSubscription = this.idle$
      .pipe(
        bufferTime(this.idleSensitivityMillisec), // Starting point of detecting of user's inactivity
        filter(
          arr => !arr.length && !this.isIdleDetected && !this.isInactivityTimer
        ),
        tap(() => {
          this.isIdleDetected = true;
          this.idleDetected$.next(true);
        }),
        switchMap(() =>
          this._ngZone.runOutsideAngular(() =>
            interval(1000).pipe(
              takeUntil(
                merge(
                  this.activityEvents$,
                  timer(this.idleMillisec).pipe(
                    tap(() => {
                      this.isInactivityTimer = true;
                      this.timerStart$.next(true);
                    })
                  )
                )
              ),
              finalize(() => {
                this.common.RemoveLocalStorage('lastActivityTime');
                this.isIdleDetected = false;
                this.idleDetected$.next(false);
              })
            )
          )
        )
      )
      .subscribe();


    this.setupTimer(this.timeout);
    this.setupPing(this.pingMillisec);
  }

  stopWatching() {
    this.stopTimer();
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
  }

  stopTimer() {
    this.isInactivityTimer = false;
    this.timerStart$.next(false);

  }

  resetTimer() {
    this.stopTimer();
    this.isTimeout = false;
  }

  /**
   * Return observable for timer's countdown number that emits after idle.
   */
  onTimerStart(): Observable<any> {
    return this.timerStart$.pipe(
      distinctUntilChanged(),
      switchMap(start => (start ? this.timer$ : of(null)))
    );
  }

  /**
   * Return observable for idle status changed
   */
  onIdleStatusChanged(): Observable<boolean> {
    return this.idleDetected$.asObservable();
  }

  /**
   * Return observable for timeout is fired.
   */
  onTimeout(): Observable<boolean> {
    return this.timeout$.pipe(
      filter(timeout => !!timeout),
      tap(() => (this.isTimeout = true)),
      map(() => true)
    );
  }

  getConfigValue(): JTUserIdleConfig {
    return {
      idle: this.idleMillisec,
      idleSensitivity: this.idleSensitivityMillisec,
      timeout: this.timeout,
      ping: this.pingMillisec
    };
  }

  /**
   * Set config values.
   * @param config
   */
  setConfigValues(config: JTUserIdleConfig) {
    if (this.idleSubscription && !this.idleSubscription.closed) {
      console.error('Call stopWatching() before set config values');
      return;
    }

    this.setConfig(config);
  }

  private setConfig(config: JTUserIdleConfig) {
    if (config.idle) {
      this.idleMillisec = config.idle * 1000;
    }
    if (config.ping) {
      this.pingMillisec = config.ping * 1000;
    }
    if (config.idleSensitivity) {
      this.idleSensitivityMillisec = config.idleSensitivity * 1000;
    }
    if (config.timeout) {
      this.timeout = config.timeout;
    }
  }

  /**
   * Set custom activity events
   *
   * @param customEvents Example: merge(
   *   fromEvent(window, 'mousemove'),
   *   fromEvent(window, 'resize'),
   *   fromEvent(document, 'keydown'),
   *   fromEvent(document, 'touchstart'),
   *   fromEvent(document, 'touchend')
   * )
   */
  setCustomActivityEvents(customEvents: Observable<any>) {
    if (this.idleSubscription && !this.idleSubscription.closed) {
      console.error('Call stopWatching() before set custom activity events');
      return;
    }

    this.activityEvents$ = customEvents;
  }

  /**
   * Setup timer.
   *
   * Counts every seconds and return n+1 and fire timeout for last count.
   * @param timeout Timeout in seconds.
   */
  protected setupTimer(timeout: number) {

      this._ngZone.runOutsideAngular(() => {

        this.timer$ = interval(1000).pipe(
          take(timeout),
          map(() => 1),
          scan((acc, n) => acc + n),
          tap(count => {
           // console.log("Timer-->>count:" + count);
            if (count === timeout) {
              this.timeout$.next(true);
            }
          })
        );
      });

  }

  protected startIdleTimer() {
    // check after every 2 seconds if user active
    this._ngZone.runOutsideAngular(() => {
      this.activityEvents$ = interval(2000).pipe(filter(ev => this.checkIfUserIdle(ev))
      );
    });
  }

  protected checkIfUserIdle(ev: number): boolean {
    let lastActivityTime = this.common.GetFromLocalStorage('lastActivityTime');

    if (!this.common.isNullOrWhiteSpace(lastActivityTime)) {
      return true;
    } else { return false; }
  }

  /**
   * Setup ping.
   *
   * Pings every ping-seconds only if is not timeout.
   * @param pingMillisec
   */
  protected setupPing(pingMillisec: number) {
    this.ping$ = interval(pingMillisec).pipe(filter(() => !this.isTimeout));
  }
}
